import React, { useState } from "react"
import PubSub from "pubsub-js"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Button from "aldoo-ra/Button"
import {
  PlusIcon,
  CreditCardIcon,
  WalletIcon,
} from "@heroicons/react/24/outline"
import Checkbox from "aldoo-ra/Checkbox"
import usePaymentSubmission from "./use-payment-submission"

const PaymentMethodIcon = ({ type }) => {
  switch (type?.toLowerCase()) {
    case "card":
      return <CreditCardIcon className="h-6 w-6 lg:h-7 lg:w-7 text-gray_2" />
    default:
      return <WalletIcon className="h-6 w-6 lg:h-7 lg:w-7 text-gray_2 mt-4 md:mt-5" />
  }
}

const PaymentMethodDetails = ({ method, texts }) => {
  if (!method) return null

  if (method?.type?.toLowerCase() === "card") {
    return (
      <div>
        <Typography 
          variant="body_24_accent"
          className="text-text my-0"
        >
          {method.brand} •••• {method.last4}
        </Typography>

        <Typography 
          variant="info_16"
          className="!text-gray_1 m-0"
        >
          {texts?.expires || "Expires"} {method.expiryMonth}/{method.expiryYear}
        </Typography>
      </div>
    )
  }

  return (
    <>
      <Typography 
        variant="body_22_accent"
        className="text-text"
      >
        {method.displayName || method.type}
      </Typography>

      {method.details && (
        <Typography 
          variant="info_16"
          className="text-gray_1 mt-0"
        >
          {method.details}
        </Typography>
      )}
    </>
  )
}

const ReusePaymentDialog = ({ store, onSelectNewPayment, isActive }) => {
  const texts = TextBundle({ id: "aldoo-store" })
  const [useSaved, setUseSaved] = useState(true)
  const [useNew, setUseNew] = useState(false)
  const [loading, setLoading] = useState(false)

  const { handleSubmitWithSavedMethod, stripe } = usePaymentSubmission(store)

  const { paymentInfo, setPaymentInfo, onPaymentError, purchaseFlow, getFragment } = store

  const CheckoutTotalBar = getFragment("checkout-total-bar")
  const { savedPaymentMethod } = paymentInfo

  // Check if Stripe is ready
  const isStripeReady = !!stripe

  const handleSavedToggle = (checked) => {
    const newValue = typeof checked === 'boolean' ? checked : !useSaved
    setUseSaved(newValue)
    if (newValue) setUseNew(false)
  }

  const handleNewToggle = (checked) => {
    const newValue = typeof checked === 'boolean' ? checked : !useNew
    setUseNew(newValue)
    if (newValue) setUseSaved(false)
  }

  const handleSubmit = async (e) => {
    e?.preventDefault()

    if (useNew) {
      // User selected "New Payment Method" - advance to payment step via PubSub
      onSelectNewPayment()

      // Advance flow to next step (payment) via PubSub
      PubSub.publish("PURCHASE_FLOW_NEXT", {
        stepData: {
          useNewPaymentMethod: true
        }
      })
      return
    }

    if (useSaved) {
      try {
        setLoading(true)
        // Build invoice data with saved billing flag for payment submission
        let invoiceDataForPayment = store.invoiceData
        if (store.preloadedInvoiceData && !store.invoiceData) {
          invoiceDataForPayment = {
            ...store.preloadedInvoiceData,
            useSavedBillingDetails: true
          }
        }
        await handleSubmitWithSavedMethod(invoiceDataForPayment)
        // Don't call onClose() - let onPaymentSuccess handle the dialog state
        // The payment success callback will either close the dialog or show success message
      } catch (error) {
        console.error("Payment failed:", error)
      } finally {
        setLoading(false)
      }
    }
  }

  // Update button state via PubSub - only when this step is active
  React.useEffect(() => {
    if (!isActive) {
      return
    }

    const buttonText = useNew ? (texts?.continue || "Continue") : (texts?.payNow || "Pay Now")
    // For "Pay Now" (useSaved), require Stripe to be ready
    const canContinue = (useSaved || useNew) && !loading && (useNew || isStripeReady)

    PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
      stepId: 'payment-reuse',
      onContinue: handleSubmit,
      onBack: () => PubSub.publish("PURCHASE_FLOW_BACK"),
      canContinue,
      loading: loading,
      continueText: buttonText,
    })
  }, [useSaved, useNew, loading, isStripeReady, isActive])

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex items-center justify-center py-8 pb-[70px] px-6 overflow-y-auto">
        <div className="w-full max-w-[468px] space-y-8">
          <div className="space-y-4">
            <div
              className={`rounded-[24px] hover:bg-primary_lt/50 transition-all px-4 py-8 cursor-pointer border ${useSaved ? 'ring-2 ring-primary bg-primary_lt/30 border-transparent' : 'border-gray-200'}`}
              onClick={handleSavedToggle}
            >
              <div className="flex items-center">
                <Checkbox
                variant="aqua"
                  isOn={useSaved}
                  onChange={handleSavedToggle}
                  label={
                    <div className="flex items-center space-x-4 ml-2 md:ml-6">
                      <PaymentMethodIcon type={savedPaymentMethod?.type} />

                      <div className="">
                        <PaymentMethodDetails method={savedPaymentMethod} texts={texts}/>
                      </div>
                    </div>
                  }
                />
              </div>
            </div>

            <div
              className={`rounded-[24px] hover:bg-primary_lt/50 transition-all px-4 py-8 cursor-pointer border ${useNew ? 'ring-2 ring-primary bg-primary_lt/30 border-transparent' : 'border-gray-200'}`}
              onClick={handleNewToggle}
            >
              <div className="flex items-center">
                <Checkbox
                  variant="aqua"
                  isOn={useNew}
                  onChange={handleNewToggle}
                  label={
                    <div className="flex items-center space-x-4 ml-2 md:ml-6">
                      <PlusIcon className="h-6 w-6 lg:h-7 lg:w-7 text-gray_2 flex-shrink-0" />

                      <div className="flex flex-col">
                        <Typography
                          variant="body_22_accent"
                          className="text-text my-0"
                        >
                          {texts?.newPaymentMethod || "New Payment Method"}
                        </Typography>

                        <Typography
                          variant="info_16"
                          className="text-gray_1 my-0"
                        >
                          {texts?.addNewCard || "Add a new payment method"}
                        </Typography>
                      </div>
                    </div>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice link + total - Sticky Bottom */}
      <CheckoutTotalBar store={store} />
    </div>
  )
}

export default ReusePaymentDialog
